import { useEffect, useState } from 'react'
import { type CreateScope, type OwnedPrompt, ownedPromptsApi } from '../../api/owned-prompts'
import Modal from '../Modal'
import PromptEditorDialog, { type PromptFields } from './PromptEditorDialog'
import PromptMarkdown from './PromptMarkdown'

interface PromptCatalogDialogProps {
  /** Where the catalog reads from. */
  source: 'organization' | 'builtin'
  /** Copy destination when source is 'organization'; ignored for 'builtin' (always organization). */
  projectId?: number
  onClose: () => void
  onSaved: () => void
}

const SOURCE_LABEL: Record<PromptCatalogDialogProps['source'], string> = {
  organization: 'Copy from organization',
  builtin: 'Import from built-in catalog',
}

/**
 * Searchable source list -> selected detail with a Markdown preview ->
 * Import/Copy opens the shared editor prefilled from the source, saved via
 * copyOwnedPrompt (never a version/binding/fork operation). The source is
 * never modified.
 */
export default function PromptCatalogDialog({
  source,
  projectId,
  onClose,
  onSaved,
}: PromptCatalogDialogProps) {
  const [query, setQuery] = useState('')
  const [items, setItems] = useState<OwnedPrompt[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [selected, setSelected] = useState<OwnedPrompt | null>(null)
  const [importing, setImporting] = useState(false)

  const destination: CreateScope & { kind: 'organization' | 'project' } =
    source === 'builtin' || projectId === undefined
      ? { kind: 'organization' }
      : { kind: 'project', projectId }

  async function load(reset: boolean) {
    setLoading(true)
    setLoadError(null)
    try {
      const page = await ownedPromptsApi.list({
        view: source,
        q: query.trim() || undefined,
        cursor: reset ? undefined : (cursor ?? undefined),
      })
      setItems((current) => (reset ? page.items : [...current, ...page.items]))
      setCursor(page.nextCursor)
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load prompts.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setSelected(null)
    void load(true)
    // Reset and reload whenever the search text changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  async function handleCopySave(fields: PromptFields) {
    if (!selected) return
    await ownedPromptsApi.copy(selected.id, destination, fields)
    onSaved()
    onClose()
  }

  if (selected && importing) {
    return (
      <PromptEditorDialog
        title={`${source === 'builtin' ? 'Import' : 'Copy'} "${selected.title}"`}
        initial={{
          title: selected.title,
          description: selected.description,
          body: selected.body,
        }}
        onSave={handleCopySave}
        onClose={() => setImporting(false)}
      />
    )
  }

  return (
    <Modal isOpen onClose={onClose} title={SOURCE_LABEL[source]} size="xl">
      {!selected ? (
        <div className="space-y-4">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by title or description…"
            aria-label="Search prompts"
            className="w-full rounded-lg border border-harbor-border bg-harbor-surface-raised px-3 py-2 text-sm text-harbor-text"
          />
          {loadError && (
            <p role="alert" className="text-sm text-red-400">
              {loadError}
            </p>
          )}
          {loading && items.length === 0 ? (
            <p className="py-8 text-center text-sm text-harbor-text-muted">Loading…</p>
          ) : items.length === 0 ? (
            <p className="py-8 text-center text-sm text-harbor-text-muted">
              No prompts found{query.trim() ? ` matching "${query.trim()}"` : ''}.
            </p>
          ) : (
            <ul className="max-h-96 divide-y divide-harbor-border overflow-y-auto rounded-lg border border-harbor-border">
              {items.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => setSelected(item)}
                    className="w-full px-4 py-3 text-left hover:bg-harbor-surface-raised"
                  >
                    <p className="text-sm font-medium text-harbor-text">{item.title}</p>
                    {item.description && (
                      <p className="mt-0.5 truncate text-xs text-harbor-text-muted">
                        {item.description}
                      </p>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {cursor && (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => void load(false)}
                disabled={loading}
                className="rounded-lg border border-harbor-border px-4 py-2 text-sm font-medium text-harbor-text-secondary hover:bg-harbor-surface-raised disabled:opacity-50"
              >
                {loading ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <button
            type="button"
            onClick={() => setSelected(null)}
            className="text-sm font-medium text-harbor-text-secondary hover:text-harbor-text"
          >
            ← Back
          </button>
          <div>
            <h3 className="text-base font-semibold text-harbor-text">{selected.title}</h3>
            {selected.description && (
              <p className="mt-1 text-sm text-harbor-text-secondary">{selected.description}</p>
            )}
          </div>
          <PromptMarkdown markdown={selected.body} />
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="rounded-lg border border-harbor-border px-4 py-2 text-sm font-medium text-harbor-text-secondary hover:bg-harbor-surface-raised"
            >
              Back
            </button>
            <button
              type="button"
              onClick={() => setImporting(true)}
              className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-500"
            >
              {source === 'builtin' ? 'Import' : 'Copy'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  )
}
